import { Article, ArticleSortType, Category } from '@wix/answers-api';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { isProduction } from '@wix/wix-run-mode';
import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { CategoryPageWrapper } from '../../components/CategoryPageWrapper';
import {
  KNOWN_ISSUES_ITEMS_PER_PAGE,
  KnownIssuesPage,
} from '../../components/KnownIssuesPage';
import { KNOWN_ISSUE_CATEGORY_ID } from '../../constants';
import {
  getCategoryArticles,
  getCategoryByUri,
  getCategoryTranslations,
  searchArticles,
} from '../../services/answers';
import { getWixDocsUrl } from '../../services/wix-docs-redirect';
import {
  ArticleSearchResultItem,
  GetWixServerSidePropsFunction,
  HttpStatusCode,
  KNOWN_ISSUES_RELEVANT_RESOLUTIONS,
  PageType,
} from '../../types';
import { findCategoryInTree } from '../../utils/categories';
import { LoggerNamespaces, createLogger } from '../../utils/logger';
import { isSledUserAgent } from '../../utils/user-agent';
import { withServerSideProps } from '../../utils/with-server-side-props';
import { useStore } from '../../zustand/stores';

type CategoryPageProps = {
  category: Category;
  categoriesArticles: Record<string, Article[]>;
  knownIssuesArticles: ArticleSearchResultItem[] | null;
  knownIssuesCount: number | null;
};

const CategoryPage: NextPage<CategoryPageProps> = ({
  category,
  categoriesArticles,
  knownIssuesArticles,
  knownIssuesCount,
}) => {
  const { locale } = useRouter();
  const httpClient = useHttpClient();
  const categoriesTree = useStore(
    (state) => state.categoriesTreeSlice.categoriesTree
  );
  const { user } = useStore((state) => state.userSlice);
  const [followedArticleIds, setFollowedArticleIds] = useState<string[]>([]);

  const isKnownIssuePage = category.id === KNOWN_ISSUE_CATEGORY_ID;

  const getFollowedArticles = useCallback(async () => {
    try {
      const { data } = await httpClient.get<string[]>('/api/article/followed', {
        params: { locale },
      });
      setFollowedArticleIds(data);
    } catch (e) {
      setFollowedArticleIds([]);
    }
  }, [httpClient, locale]);

  useEffect(() => {
    void (async () => {
      if (isKnownIssuePage && user !== null) {
        await getFollowedArticles();
      }
    })();
  }, [getFollowedArticles, isKnownIssuePage, user]);

  return isKnownIssuePage && knownIssuesArticles ? (
    <KnownIssuesPage
      initKnownIssues={knownIssuesArticles}
      numberOfKnownIssues={knownIssuesCount || 0}
      categoryTree={categoriesTree}
      followedKnownIssuesIds={followedArticleIds}
    />
  ) : (
    <CategoryPageWrapper
      key={category.id}
      currentCategory={category}
      categories={categoriesTree}
      categoriesArticles={categoriesArticles}
    />
  );
};

export const getServerSideProps: GetWixServerSidePropsFunction =
  withServerSideProps(async ({ req, res, locale, query, helpcenterData, timer }) => {
    const { categoryName } = query;
    const logger = createLogger(LoggerNamespaces.Server);

    let category: Category;
    try {
      category = await getCategoryByUri(
        req.aspects,
        res.locals.appContext,
        `/${categoryName}`,
        locale,
        res.locals.redisClient,
        helpcenterData.categories
      );
    } catch (e: any) {
      const statusError = e.httpStatusCode || e.statusCode || e.code;
      const fullUrl = `https://support.wix.com/${locale}/${categoryName}`;

      if (statusError === HttpStatusCode.NOT_FOUND) {
        const wixDocsUrl = await getWixDocsUrl(res.locals.appContext, fullUrl);

        if (wixDocsUrl) {
          const userAgent = req.headers['user-agent'];
          if (isProduction() && userAgent && !isSledUserAgent(userAgent)) {
            const logData = {
              from: fullUrl,
              to: wixDocsUrl,
              referrer: req.headers.referer,
            };
            logger.info('Redirecting to Wix Docs', logData);
          }

          return {
            redirect: {
              statusCode: 301,
              destination: wixDocsUrl,
            },
          };
        }
      }
      throw e;
    }

    if (category.uri !== `/${categoryName}`) {
      return {
        redirect: {
          statusCode: HttpStatusCode.MOVED_PERMANENTLY,
          destination: encodeURI(`/${locale}/${category.uri}`),
        },
      };
    }

    const { aspects } = req;

    const { categories: categoriesTree } = helpcenterData;

    category = findCategoryInTree(categoriesTree, category.uri) as Category;

    if (!category) {
      logger
        .withRequest(req)
        .error(
          `Category /${locale}/${categoryName} cannot be not found in tree`
        );
      return {
        notFound: true,
      };
    }

    const isKnownIssueCategory = category.id === KNOWN_ISSUE_CATEGORY_ID;

    const articleCategoryIds = (() => {
      if (isKnownIssueCategory) {
        return [];
      }
      const children = category.children ?? [];
      if (children.length === 0) {
        return [category.id];
      }
      return children
        .filter((child) => !child.children || child.children.length === 0)
        .map((child) => child.id);
    })();

    const [categoriesArticles, translations, knownIssuesArticles] =
      await Promise.all([
        articleCategoryIds?.length
          ? timer.track('categoryArticles', () =>
            getCategoryArticles(
              aspects,
              res.locals.appContext,
              articleCategoryIds,
              locale,
              res.locals.redisClient
            )
          )
          : Promise.resolve<Record<string, Article[]>>({}),
        timer.track('categoryTranslations', () =>
          getCategoryTranslations(
            aspects,
            res.locals.appContext,
            category.id,
            res.locals.redisClient
          )
        ),
        isKnownIssueCategory
          ? timer.track('knownIssues', () =>
            searchArticles(
              aspects,
              res.locals.appContext,
              locale,
              {
                page: 1,
                pageSize: KNOWN_ISSUES_ITEMS_PER_PAGE,
                resolutions: KNOWN_ISSUES_RELEVANT_RESOLUTIONS,
                sortType: ArticleSortType.CREATION_DATE_DESC,
                text: '',
              },
              res.locals.redisClient
            )
          )
          : Promise.resolve(undefined),
      ]);

    return {
      props: {
        pageType: isKnownIssueCategory
          ? PageType.KnownIssues
          : PageType.Category,
        category,
        categoriesTree,
        categoriesArticles,
        pageData: {
          category,
          translations,
        },
        itemId: category.id,
        knownIssuesArticles: knownIssuesArticles?.items || null,
        knownIssuesCount: knownIssuesArticles?.itemsCount || null,
      },
    };
  });

export default CategoryPage;
